// DEPLOY: /var/www/woody.wtf/frontend/src/pages/TradingMovers.js
// Trading > Announcement Movers: announcements since last EOD close per exchange.
// Entries are IBKR first post-release trades; quotes arrive in paced snapshot batches.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import TradingTabs from '../components/TradingTabs';
import NoveltyTag from '../components/NoveltyTag';
import ThematicTags from '../components/ThematicTags';
import api from '../api';
import { s, errText, useTradingAccount, StatusBar } from '../components/tradingShared';

const QUOTE_BATCH = 10;
const QUOTE_REFRESH_MS = 7000;
const RELEASE_REFRESH_MS = 60000;
const SOURCES = { asx: 'ASX', sec: 'EDGAR', bw: 'Business Wire', gnw: 'GlobeNewswire', prn: 'PR Newswire', anw: 'ACCESS Newswire', nfc: 'Newsfile', dod: 'Dept of War', insider: 'Insider buying' };
const valid = v => v !== null && v !== undefined && Number.isFinite(Number(v)) && Number(v) > 0;
const price = v => valid(v) ? Number(v).toLocaleString('en-AU', {
  minimumFractionDigits: Number(v) < 1 ? 3 : 2,
  maximumFractionDigits: Number(v) < 0.1 ? 5 : Number(v) < 1 ? 4 : 2,
}) : '—';
const delta = (now, before) => valid(now) && valid(before) ? (Number(now) / Number(before) - 1) * 100 : null;
const signed = p => p === null ? '—' : (p > 0 ? '+' : '') + p.toFixed(2) + '%';
const tone = p => p === null ? 'var(--text-muted)' : p >= 0 ? 'var(--success)' : 'var(--error)';
const dirClass = a => ['positive', 'negative'].includes(a.direction) ? `dir-${a.direction}`
  : ['mixed', 'neutral'].includes(a.direction) ? 'dir-mixed' : '';
const stamp = t => t ? new Date(t).toLocaleString('en-AU', {
  day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
}) : '—';

const CSS = [
  '.am-head {display:flex;align-items:baseline;justify-content:space-between;gap:12px;flex-wrap:wrap;margin:18px 0}',
  '.am-head h2 {font-size:20px;font-weight:600;margin:0}',
  '.am-muted {font-size:12px;color:var(--text-muted)}',
  '.am-filters {display:flex;align-items:end;flex-wrap:wrap;gap:10px;margin-bottom:16px}',
  '.am-filter {display:flex;flex-direction:column;gap:5px;font-size:11px;color:var(--text-muted)}',
  '.am-filter select,.am-filter input {min-height:35px;background:var(--surface);border:1px solid var(--border);border-radius:var(--radius);padding:7px 10px;color:var(--text);font:12px inherit}',
  '.am-checks {display:flex;align-items:center;gap:10px;min-height:35px;font-size:12px;color:var(--text)}',
  '.am-checks label {display:flex;align-items:center;gap:4px;white-space:nowrap}',
  '.am-info {display:flex;align-items:center;gap:8px 18px;flex-wrap:wrap;margin-bottom:12px;color:var(--text-muted);font-size:12px}',
  '.am-card {background:var(--surface);border:1px solid var(--border);border-radius:calc(var(--radius) * 2)}',
  '.am-scroll {overflow-x:auto}',
  '.am-table {border-collapse:collapse;width:100%;font-size:12px;white-space:nowrap}',
  '.am-table th {padding:12px 9px;font:500 10px "DM Mono",monospace;letter-spacing:.06em;text-transform:uppercase;color:var(--text-muted);text-align:right;border-bottom:1px solid var(--border);background:var(--surface)}',
  '.am-table th:first-child,.am-table th:nth-child(2),.am-table th:nth-child(3) {text-align:left}',
  '.am-table td {border-bottom:1px solid var(--border);padding:12px 9px;vertical-align:middle;text-align:right}',
  '.am-table td:first-child,.am-table td:nth-child(2),.am-table td:nth-child(3) {text-align:left}',
  '.am-table tbody tr:hover {background:var(--bg)}',
  '.am-table tbody tr.dir-positive,.am-mobile-card.dir-positive {background:var(--pos-faint)}',
  '.am-table tbody tr.dir-negative,.am-mobile-card.dir-negative {background:var(--neg-faint)}',
  '.am-table tbody tr.dir-mixed,.am-mobile-card.dir-mixed {background:var(--mix-faint)}',
  '.am-table tbody tr.dir-positive:hover {background:var(--pos-hover)}',
  '.am-table tbody tr.dir-negative:hover {background:var(--neg-hover)}',
  '.am-table tbody tr.dir-mixed:hover {background:var(--mix-hover)}',
  '.am-code {font:600 13px "DM Mono",monospace;color:var(--accent);text-decoration:none}',
  '.am-code:hover {text-decoration:underline}',
  '.am-name {display:block;font-size:10px;color:var(--text-muted);max-width:155px;overflow:hidden;text-overflow:ellipsis;margin-top:2px}',
  '.am-price {font-family:"DM Mono",monospace;font-variant-numeric:tabular-nums}',
  '.am-percent {font:600 13px "DM Mono",monospace;font-variant-numeric:tabular-nums}',
  '.am-pill {display:inline-block;padding:3px 7px;background:var(--bg);border:1px solid var(--border);border-radius:6px;font:500 11px "DM Mono",monospace}',
  '.am-sub {display:block;margin-top:5px;color:var(--text-muted);font-size:10px}',
  '.am-expand {border:1px solid var(--border);border-radius:var(--radius);padding:3px 7px;background:var(--surface);color:var(--text-muted);cursor:pointer;margin-left:8px;font-size:11px}',
  '.am-news {display:grid;gap:10px;padding:15px;background:var(--bg);white-space:normal}',
  '.am-newsitem {border-left:2px solid var(--border);padding:5px 12px;font-size:12px}',
  '.am-newsitem a {color:var(--text);text-decoration:none}',
  '.am-newsitem a:hover {color:var(--accent);text-decoration:underline}',
  '.am-newsitem p {margin:6px 0 0;color:var(--text-muted);line-height:1.55}',
  '.am-mobile {display:none}',
  '.am-mobile-card {padding:14px;border-bottom:1px solid var(--border)}',
  '.am-mobile-card:last-child {border-bottom:0}',
  '.am-metrics {display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px;padding:12px 0}',
  '.am-metrics span {display:block;font-size:10px;color:var(--text-muted);margin-bottom:5px}',
  '.am-metrics strong {font:600 13px "DM Mono",monospace}',
  '@media(max-width:767px) {.am-desktop{display:none}.am-mobile{display:block}.am-filter{flex:1 1 42%}.am-filter.search{flex:1 1 100%}.am-filters{gap:8px}}',
].join('\n');

function NewsList({ items }) {
  return <div className="am-news">
    {items.map(a => <div className="am-newsitem" key={a.source + ':' + a.id + ':' + a.instrument_id}>
      <div className="am-muted">{stamp(a.announced_at)} · {SOURCES[a.source] || a.source} · Rating {a.significance || '—'}/5 · {a.direction || 'unrated'}</div>
      <div style={{ margin:'5px 0' }}>
        {a.url ? <a href={a.url} target="_blank" rel="noopener noreferrer">{a.headline} ↗</a> : a.headline}
      </div>
      <NoveltyTag n={a.novelty} /> <ThematicTags t={a.thematics} />
      {a.summary && <p>{a.summary}</p>}
      {a.entry_at && <div className="am-muted" style={{ marginTop:6 }}>First trade {stamp(a.entry_at)} at {price(a.entry_price)}</div>}
    </div>)}
  </div>;
}

export default function TradingMovers() {
  const t = useTradingAccount();
  const [market, setMarket] = useState('');
  const [source, setSource] = useState('');
  const [sigs, setSigs] = useState([3, 4, 5]);
  const [thematic, setThematic] = useState('');
  const [sensitive, setSensitive] = useState(false);
  const [rated, setRated] = useState(false);
  const [search, setSearch] = useState('');
  const [labels, setLabels] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [quotes, setQuotes] = useState({});
  const [since, setSince] = useState({});
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [quoteError, setQuoteError] = useState('');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [now, setNow] = useState(Date.now());
  const stocksRef = useRef([]);
  const cursor = useRef(0);

  useEffect(() => { document.title = 'Announcement Movers | woody.wtf'; }, []);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    api.get('/company/thematics').then(r => setLabels(r.data.labels || [])).catch(() => {});
  }, []);
  const sigKey = sigs.join(',');
  const toggleSig = n => setSigs(values => values.includes(n)
    ? values.filter(x => x !== n) : [...values, n].sort());

  useEffect(() => {
    if (!t.broker) return;
    let active = true;
    const load = async () => {
      const params = { sig: sigKey };
      if (market) params.market = market;
      if (source) params.source = source;
      if (thematic) params.thematic = thematic;
      if (sensitive) params.sensitive = 1;
      if (rated) params.rated = 1;
      try {
        const data = (await api.get('/trading/' + t.broker + '/announcement-movers', { params })).data;
        if (!active) return;
        stocksRef.current = data.stocks;
        setStocks(data.stocks);
        setSince(data.since);
        setError('');
      } catch (e) { if (active) setError(errText(e)); }
      finally { if (active) setLoading(false); }
    };
    setLoading(true);
    load();
    const timer = setInterval(load, RELEASE_REFRESH_MS);
    return () => { active = false; clearInterval(timer); };
  }, [t.broker, market, source, thematic, sensitive, rated, sigKey]);

  useEffect(() => {
    if (!t.broker) return;
    let active = true;
    let timer;
    const poll = async () => {
      const started = Date.now();
      const items = [...stocksRef.current].sort((a, b) =>
        Number(valid(b.announcements[0]?.entry_price)) - Number(valid(a.announcements[0]?.entry_price)));
      if (items.length && !document.hidden) {
        const from = cursor.current % items.length;
        const batch = [...items.slice(from), ...items.slice(0, from)].slice(0, QUOTE_BATCH);
        cursor.current = (from + batch.length) % items.length;
        try {
          const data = (await api.get('/trading/' + t.broker + '/quotes', {
            params: { codes: batch.map(x => x.code).join(',') },
          })).data;
          if (!active) return;
          setQuotes(previous => {
            const next = { ...previous };
            data.quotes.forEach(q => {
              next[q.code] = q.error
                ? { ...previous[q.code], error: q.error }
                : { ...q, received_at: new Date().toISOString() };
            });
            return next;
          });
          setLastUpdate(new Date());
          setQuoteError('');
        } catch (e) { if (active) setQuoteError(errText(e)); }
      }
      if (active) timer = setTimeout(poll, Math.max(1000, QUOTE_REFRESH_MS - (Date.now() - started)));
    };
    poll();
    return () => { active = false; clearTimeout(timer); };
  }, [t.broker]);

  const rows = useMemo(() => stocks.filter(x => {
    if (!search.trim()) return true;
    const needle = search.toUpperCase().trim();
    return x.ticker.includes(needle) || (x.name || '').toUpperCase().includes(needle);
  }).map(stock => {
    const announcement = stock.announcements[0];
    const quote = quotes[stock.code] || {};
    return {
      ...stock, a: announcement, q: quote,
      move: delta(quote.last_price, announcement.entry_price),
      closeMove: delta(quote.last_price, quote.prev_close_price),
    };
  }).sort((a, b) => {
    if (a.move === null) return b.move === null ? a.code.localeCompare(b.code) : 1;
    if (b.move === null) return -1;
    return b.move - a.move;
  }), [stocks, quotes, search]);

  const toggle = code => setExpanded(x => ({ ...x, [code]: !x[code] }));
  const age = timestamp => {
    const elapsed = Math.max(0, Math.floor((now - Date.parse(timestamp)) / 1000));
    if (!Number.isFinite(elapsed)) return null;
    return elapsed < 60 ? `${elapsed}s` : elapsed < 3600 ? `${Math.floor(elapsed / 60)}m` : `${Math.floor(elapsed / 3600)}h`;
  };
  const dataType = q => <span className="am-sub" title={q.error || ''}>
    {q.error ? 'quote unavailable' : q.market_data_type || 'awaiting quote'}
    {q.update_time && age(q.update_time) !== null ? ` · IBKR update ${age(q.update_time)} ago` : ''}
    {q.received_at ? ` · checked ${age(q.received_at)} ago` : ''}
    {!valid(q.last_price) && ' · no last trade'}
  </span>;
  const rating = a => <><span className="am-pill">{a.significance || '—'}/5</span>
    <span className="am-sub" style={{ textTransform:'capitalize' }}>{a.direction || 'unrated'}</span></>;
  const entry = a => <><span className="am-price">{price(a.entry_price)}</span>
    <span className="am-sub">{a.entry_at ? stamp(a.entry_at) : 'not yet available'}</span></>;

  return <div style={s.page}>
    <style>{CSS}</style>
    <Navbar />
    <div style={s.content}>
      <h1 style={s.heading}>Trading</h1>
      <TradingTabs />
      <StatusBar t={t} />
      <div className="am-head">
        <div><h2>Announcement Movers</h2>
          <div className="am-muted">ASX and US announcements since each exchange's last completed EOD session.</div></div>
        <span className="am-muted">IBKR snapshots · up to {QUOTE_BATCH} stocks per batch, minimum {QUOTE_REFRESH_MS / 1000}s between starts</span>
      </div>
      <div className="am-filters">
        <label className="am-filter search">Ticker or company
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search stocks" />
        </label>
        <label className="am-filter">Market
          <select value={market} onChange={e => setMarket(e.target.value)}>
            <option value="">AU + US</option><option value="AU">ASX</option><option value="US">US</option>
          </select>
        </label>
        <label className="am-filter">Source
          <select value={source} onChange={e => setSource(e.target.value)}>
            <option value="">All sources</option>
            {Object.entries(SOURCES).map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
        </label>
        <label className="am-filter">Thematics
          <select value={thematic} onChange={e => setThematic(e.target.value)}>
            <option value="">All labels</option><option value="any">Any label</option>
            {labels.map(name => <option key={name} value={name}>{name}</option>)}
          </select>
        </label>
        <div className="am-filter">AI importance
          <div className="am-checks">
            {[1,2,3,4,5].map(n => <label key={n}><input type="checkbox" checked={sigs.includes(n)}
              onChange={() => toggleSig(n)} />{n}</label>)}
          </div>
        </div>
        <div className="am-checks">
          <label><input type="checkbox" checked={sensitive} onChange={e => setSensitive(e.target.checked)} /> Price sensitive</label>
          <label><input type="checkbox" checked={rated} onChange={e => setRated(e.target.checked)} /> AI rated only</label>
        </div>
      </div>
      <div className="am-info">
        <strong style={{ color:'var(--text)' }}>{rows.length} stocks</strong>
        <span>ASX since {stamp(since.AU)}</span>
        <span>US since {stamp(since.US)}</span>
        <span>Sorted by % since announcement ↓</span>
        <span>Row shading shows announcement sentiment, not price movement</span>
        {lastUpdate && <span>Quotes updated {lastUpdate.toLocaleTimeString('en-AU')}</span>}
      </div>
      {error && <div style={s.err}>{error}</div>}
      {quoteError && <div className="am-muted" style={{ marginBottom:12 }}>IBKR: {quoteError}</div>}
      {loading ? <div className="am-card" style={{ padding:25, textAlign:'center' }}>Loading announcements…</div>
        : rows.length === 0 ? <div className="am-card" style={{ padding:25, textAlign:'center', color:'var(--text-muted)' }}>
          No stocks match these announcement filters.
        </div> : <div className="am-card">
          <div className="am-scroll am-desktop">
            <table className="am-table">
              <thead><tr>
                <th>Stock</th><th>Rating / labels</th><th>Announcement</th>
                <th>Entry price</th><th>Latest</th><th>Since announcement ↓</th>
                <th>Session open</th><th>Prev close</th><th>Since close</th>
              </tr></thead>
              <tbody>{rows.map(row => <React.Fragment key={row.code}>
                <tr className={dirClass(row.a)}>
                  <td><Link className="am-code" to={'/company?ticker=' + encodeURIComponent(row.ticker)} target="_blank" rel="noopener noreferrer">{row.ticker}</Link>
                    <span className="am-name" title={row.name}>{row.name}</span></td>
                  <td>{rating(row.a)}
                    <div style={{ marginTop:5 }}><NoveltyTag n={row.a.novelty} /> <ThematicTags t={row.a.thematics} /></div></td>
                  <td style={{ maxWidth:280, whiteSpace:'normal' }}>
                    <div style={{ fontSize:11, lineHeight:1.45 }}>{row.a.headline}</div>
                    <span className="am-sub">{stamp(row.a.announced_at)} · {SOURCES[row.a.source] || row.a.source}
                      {row.announcements.length > 1 ? ' · ' + row.announcements.length + ' releases' : ''}</span>
                    <button className="am-expand" onClick={() => toggle(row.code)}>
                      {expanded[row.code] ? '− Hide' : '+ Details'}</button>
                  </td>
                  <td>{entry(row.a)}</td>
                  <td><span className="am-price">{price(row.q.last_price)}</span>{dataType(row.q)}</td>
                  <td><span className="am-percent" style={{ color:tone(row.move) }}>{signed(row.move)}</span></td>
                  <td className="am-price">{price(row.q.open_price)}</td>
                  <td className="am-price">{price(row.q.prev_close_price)}</td>
                  <td><span className="am-percent" style={{ color:tone(row.closeMove) }}>{signed(row.closeMove)}</span></td>
                </tr>
                {expanded[row.code] && <tr className={dirClass(row.a)}><td colSpan={9} style={{ padding:0, textAlign:'left' }}>
                  <NewsList items={row.announcements} /></td></tr>}
              </React.Fragment>)}</tbody>
            </table>
          </div>
          <div className="am-mobile">{rows.map(row => <div key={row.code} className={`am-mobile-card ${dirClass(row.a)}`}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'start', gap:12 }}>
              <div><Link className="am-code" to={'/company?ticker=' + encodeURIComponent(row.ticker)} target="_blank" rel="noopener noreferrer">{row.ticker}</Link>
                <span className="am-name">{row.name}</span></div>
              <div style={{ textAlign:'right' }}><strong className="am-percent" style={{ color:tone(row.move) }}>
                {signed(row.move)}</strong><span className="am-sub">Since announcement</span></div>
            </div>
            <div style={{ marginTop:8 }}>{rating(row.a)} <NoveltyTag n={row.a.novelty} /> <ThematicTags t={row.a.thematics} /></div>
            <div className="am-metrics">
              <div><span>Announcement</span><strong>{price(row.a.entry_price)}</strong></div>
              <div><span>Latest</span><strong>{price(row.q.last_price)}</strong></div>
              <div><span>Since close</span><strong style={{ color:tone(row.closeMove) }}>{signed(row.closeMove)}</strong></div>
              <div><span>Session open</span><strong>{price(row.q.open_price)}</strong></div>
              <div><span>Prev close</span><strong>{price(row.q.prev_close_price)}</strong></div>
              <div><span>Quote</span>{dataType(row.q)}</div>
            </div>
            <div className="am-muted">{stamp(row.a.announced_at)} · {row.a.headline}</div>
            <button className="am-expand" style={{ margin:'9px 0 0' }} onClick={() => toggle(row.code)}>
              {expanded[row.code] ? '− Hide' : '+ Details'} ({row.announcements.length})</button>
            {expanded[row.code] && <NewsList items={row.announcements} />}
          </div>)}</div>
        </div>}
      <div className="am-muted" style={{ marginTop:12 }}>
        Entry is the first one-minute trade after a release, not its publication-time price.
        Missing entries show —; delayed or unavailable IBKR data is labelled.
        Market-session boundaries follow the latest stored EOD calendar.
      </div>
    </div>
  </div>;
}
